import * as THREE from 'three';

/** Viewport editor cameras — keep in sync with GridPage perspective setup. */
export const VIEWPORT_CAMERA_FOV = 50;
export const VIEWPORT_CAMERA_NEAR = 0.01;
export const VIEWPORT_CAMERA_FAR = 5000;

const _target = new THREE.Vector3();

/**
 * Orthographic frustum in pixel-like units (matches GridPage OrbitControls setup).
 * Visible world height at zoom z = (top - bottom) / z.
 */
export function updateOrthographicFrustum(camera, width, height) {
  if (!camera?.isOrthographicCamera) return;
  camera.left = width / -2;
  camera.right = width / 2;
  camera.top = height / 2;
  camera.bottom = height / -2;
}

/**
 * Zoom on the ortho camera that matches the current perspective view
 * (same orbit target, FOV, and camera-to-target distance).
 */
export function perspectiveToOrthographicZoom(
  perspectiveCamera,
  orbitTarget,
  viewportWidth,
  viewportHeight
) {
  if (!perspectiveCamera?.isPerspectiveCamera) return 50;

  const target = orbitTarget || _target.set(0, 0, 0);
  const distance = Math.max(
    perspectiveCamera.position.distanceTo(target),
    0.001
  );
  const fovRad = THREE.MathUtils.degToRad(
    perspectiveCamera.fov ?? VIEWPORT_CAMERA_FOV
  );
  const visibleHeight = 2 * distance * Math.tan(fovRad / 2);
  const frustumHeight = Math.max(viewportHeight, 1);
  return THREE.MathUtils.clamp(frustumHeight / visibleHeight, 0.1, 500);
}

/**
 * When switching viewport cameras, copy pose + match framing — do not reset zoom to a constant.
 */
export function syncViewportCameraSwitch(
  fromCamera,
  toCamera,
  orbitTarget,
  viewportWidth,
  viewportHeight
) {
  if (!fromCamera || !toCamera || fromCamera === toCamera) return;

  const w = Math.max(viewportWidth || window.innerWidth, 1);
  const h = Math.max(viewportHeight || window.innerHeight, 1);
  const target = orbitTarget || _target.set(0, 0, 0);

  toCamera.position.copy(fromCamera.position);
  toCamera.quaternion.copy(fromCamera.quaternion);
  toCamera.near = fromCamera.near ?? VIEWPORT_CAMERA_NEAR;
  toCamera.far = fromCamera.far ?? VIEWPORT_CAMERA_FAR;

  if (toCamera.isOrthographicCamera && fromCamera.isPerspectiveCamera) {
    updateOrthographicFrustum(toCamera, w, h);
    toCamera.zoom = perspectiveToOrthographicZoom(
      fromCamera,
      target,
      w,
      h
    );
  } else if (toCamera.isPerspectiveCamera && fromCamera.isOrthographicCamera) {
    toCamera.fov = fromCamera.fov ?? VIEWPORT_CAMERA_FOV;
    toCamera.aspect = w / h;
  } else if (toCamera.isOrthographicCamera && fromCamera.isOrthographicCamera) {
    updateOrthographicFrustum(toCamera, w, h);
    toCamera.zoom = fromCamera.zoom;
  } else if (toCamera.isPerspectiveCamera && fromCamera.isPerspectiveCamera) {
    toCamera.fov = fromCamera.fov ?? VIEWPORT_CAMERA_FOV;
    toCamera.aspect = fromCamera.aspect ?? w / h;
  }

  toCamera.updateProjectionMatrix();
}

/** Apply shared clip planes + FOV to both viewport cameras on init. */
export function applyViewportCameraDefaults(perspectiveCamera, orthographicCamera) {
  if (perspectiveCamera) {
    perspectiveCamera.fov = VIEWPORT_CAMERA_FOV;
    perspectiveCamera.near = VIEWPORT_CAMERA_NEAR;
    perspectiveCamera.far = VIEWPORT_CAMERA_FAR;
    perspectiveCamera.updateProjectionMatrix();
  }
  if (orthographicCamera) {
    orthographicCamera.near = VIEWPORT_CAMERA_NEAR;
    orthographicCamera.far = VIEWPORT_CAMERA_FAR;
    updateOrthographicFrustum(
      orthographicCamera,
      window.innerWidth,
      window.innerHeight
    );
  }
}
